import React, { useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import IoIosArrowBack from "../../../assets/icons/arrowLeft";
import { handleApiRequest } from "../../../services/handleApiRequest";
import { register, resendOtp, verifyEmail } from "../../../redux/auth/thunk";
import MyForm from "../../../Component/formComponent";
import { signinRoute, signupRoute } from "../../../routes/routesPath";
import { otpValidationSchema, registrationSchema } from "../../../utils/validationSchemas";
import SideSection from "../components/sideSection";
import { useFormik } from "formik";
import { successMsg } from "../../../utils/toastMessage";
import { handleTransformValues } from "../../../utils/formatersAndParsers";
import AppLogo from "../../../Component/appLogo";
import { getOTPExpiryTime } from "../../../utils/helpers";
import MyHelmet from "../../../seo/helmet";
import Asterik from "../../../Component/common/asterik";
import { TextInput } from "../../../Component/formComponent/components/inputFields";
import { ErrorField } from "../../../Component/common/errorField";

const registrationFields = [
  {
    value: "name",
    type: "text",
    placeholder: "Enter Full Name",
    label: "Full Name",
    isRequired: true,
    wrapperClassName: "my-3",
  },
  {
    value: "email",
    type: "email",
    placeholder: "Enter Email Address",
    isRequired: true,
    wrapperClassName: "my-3",
  },
  {
    value: "mobile",
    type: "phone",
    isRequired: true,
    wrapperClassName: "my-3",
  },
  // { value: "birth_date", type: "date" },
  // { value: "gender", type: "select", options: genderOptions },
  // { value: "address", type: "text" },
  { value: "avatar", type: "file", wrapperClassName: "my-3" },
  { value: "password", type: "password", isRequired: true, wrapperClassName: "my-3" },
  {
    value: "confirmPassword",
    type: "password",
    placeholder: "Confirm Password",
    isRequired: true,
    wrapperClassName: "my-3",
  },
];

const defaultValues = {
  name: "",
  email: "",
  mobile: "",
  avatar: "",
  password: "",
  confirmPassword: "",
};

const Signup = () => {
  const { totalSeconds, start, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: getOTPExpiryTime(),
  });

  const navigate = useNavigate();
  const [registrationStep, setRegistrationStep] = useState({ step: 1 });
  const [otpDetails, setOtpDetails] = useState({});

  const handleRegistration = async () => {
    try {
      setRegistrationStep((prev) => ({ ...prev, action: "registering" }));

      const request = await handleTransformValues(values);
      const response = await handleApiRequest(register, request);

      if (response.status) {
        setRegistrationStep((prev) => ({ ...prev, action: "", step: 2 }));
        start();
        setOtpDetails((prev) => ({ ...prev, email: values.email }));
      } else {
        setRegistrationStep((prev) => ({ ...prev, action: "" }));
      }
    } catch (error) {
      setRegistrationStep((prev) => ({ ...prev, action: "" }));
    }
  };

  const handleEmailVerification = async () => {
    setRegistrationStep((prev) => ({ ...prev, action: "verifyingEmail" }));

    const request = {
      email: otpDetails.email,
      otp: otpForm.values.otp,
    };

    const response = await handleApiRequest(verifyEmail, request);
    if (response.status) {
      navigate(signinRoute);
    }
    setRegistrationStep((prev) => ({ ...prev, action: "" }));
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    setSubmitting,
    isSubmitting,
  } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: defaultValues,
    validationSchema: registrationSchema,
    onSubmit: handleRegistration,
  });

  const otpForm = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpValidationSchema,
    onSubmit: () => {
      handleEmailVerification();
    },
  });

  const handleResendOtp = async () => {
    setRegistrationStep((prev) => ({ ...prev, action: "sendingOtp" }));

    const response = await handleApiRequest(resendOtp, { email: otpDetails.email });
    if (response.status) {
      successMsg("OTP sent!!");
      restart(getOTPExpiryTime());
    }
    setRegistrationStep((prev) => ({ ...prev, action: "" }));
  };

  return (
    <>
      <MyHelmet
        title={"Join to connect with emotional support community"}
        keywords={[]}
        description="Feelinghub is an anonymous social platform to share feelings online or post life stories anonymously for free. It is a safe space to express yourself."
        link={signupRoute}
      />

      <section className="position-relative">
        <div>
          <Row className="authContainer m-0">
            <SideSection />
            <Col lg={5} className="authFormContainer  px-3">
              <div className="my-5 w-100">
                <AppLogo />
                <div className="position-relative mt-3 w-100">
                  {registrationStep?.step === 1 ? (
                    <Form onSubmit={handleSubmit}>
                      <MyForm
                        values={values}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        formFields={registrationFields}
                        setSubmitting={setSubmitting}
                      />

                      {registrationStep?.action === "registering" ? (
                        <button className="primaryBtn muted rounded w-100 mt-3 py-1">
                          Creating your account
                          <i className="growLoader spinner-grow ms-2" role="status"></i>
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="primaryBtn rounded w-100 py-1 mt-3"
                          disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              Uploading your avatar
                              <i className="growLoader spinner-grow ms-2" role="status"></i>
                            </>
                          ) : (
                            "Sign Up"
                          )}
                        </button>
                      )}

                      <div className="py-3">
                        <p>
                          Already have an account?
                          <Link to={signinRoute} className="text-decoration-none">
                            {" "}
                            Login
                          </Link>
                        </p>
                      </div>
                    </Form>
                  ) : registrationStep?.step === 2 ? (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <p
                          className="backBtn mb-0"
                          onClick={() => setRegistrationStep((prev) => ({ ...prev, step: 1 }))}>
                          <IoIosArrowBack />
                        </p>
                        <h4 className="mb-0">Email Verification</h4>
                      </div>

                      <p className="mb-4">
                        We have sent a 6 digits OTP on your email. Enter the OTP to verify your
                        email address.
                      </p>

                      <Form onSubmit={otpForm.handleSubmit}>
                        <div className="position-relative my-3">
                          <label htmlFor="email" className="text-color mb-1">
                            Email
                            <Asterik />
                          </label>

                          <TextInput
                            placeholder="Enter Email Address"
                            value={values.email}
                            isDisabled={true}
                          />
                        </div>

                        <div className="my-2 mt-3">
                          <label htmlFor="otp" className="text-color mb-1">
                            OTP
                            <Asterik />
                          </label>

                          <TextInput
                            name="otp"
                            placeholder="Enter OTP Sent to Your Email"
                            value={otpForm.values.otp}
                            onChange={otpForm.handleChange}
                          />
                          <ErrorField error={otpForm.touched.otp ? otpForm.errors.otp : ""} />
                        </div>

                        {registrationStep?.action === "verifyingEmail" ? (
                          <button className="primaryBtn muted rounded w-100 mt-3 py-1">
                            Verifying your Email
                            <i className="growLoader spinner-grow ms-2" role="status"></i>
                          </button>
                        ) : (
                          <button type="submit" className="primaryBtn rounded w-100 mt-3 py-1">
                            Verify Email
                          </button>
                        )}
                      </Form>

                      <div className="text-medium pb-4 pt-2 d-flex align-items-center gap-2">
                        <p className="m-0">Didn't receive OTP?</p>
                        {totalSeconds > 0 ? (
                          <span className="text-primary">{totalSeconds} sec</span>
                        ) : registrationStep.action === "sendingOtp" ? (
                          <button className="textBtn text-primary">Sending OTP...</button>
                        ) : (
                          <button
                            className="textBtn text-primary fw-bold"
                            onClick={handleResendOtp}>
                            Resend
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>
    </>
  );
};

export default Signup;
